import React, { useState, useMemo } from 'react';
import { Check, Plus, Trash2, ShieldCheck } from 'lucide-react';

const DEFAULT_KIT = [
  {
    category: 'Beauty',
    items: [
      { name: 'Hair pins / bobby pins', checked: false },
      { name: 'Hairspray', checked: false },
      { name: 'Deodorant', checked: false },
      { name: 'Leppestift / lip balm', checked: false },
      { name: 'Pudder / blotting papers', checked: false },
      { name: 'Neglelim / klar neglelakk', checked: false },
      { name: 'Q-tips / bomullspads', checked: false },
      { name: 'Parfyme', checked: false },
    ],
  },
  {
    category: 'Clothing',
    items: [
      { name: 'Sewing kit / needle & thread', checked: false },
      { name: 'Safety pins (flere sizer)', checked: false },
      { name: 'Fashion tape / dobbeltsidig tape', checked: false },
      { name: 'Flekkfjerner / Vanish stift', checked: false },
      { name: 'Shoeskrem / pusseklut', checked: false },
      { name: 'Extra tights', checked: false },
      { name: 'Kleshenger', checked: false },
      { name: 'Dampstryker / garment steamer', checked: false },
    ],
  },
  {
    category: 'Health & Firsthjelp',
    items: [
      { name: 'Smertestillende (Paracet / Ibux)', checked: false },
      { name: 'Band-aids (flere sizer)', checked: false },
      { name: 'Blister plasters', checked: false },
      { name: 'Allrgymedisiner (antihistaminer)', checked: false },
      { name: 'Magesyretabletter', checked: false },
      { name: 'Hand sanitizer / disinfectant', checked: false },
      { name: 'Reisesykepose', checked: false },
      { name: 'Tampong / bind', checked: false },
      { name: 'Eye drops', checked: false },
      { name: 'Solkrem', checked: false },
      { name: 'Insektmiddel', checked: false },
      { name: 'Tissues / wet wipes', checked: false },
    ],
  },
  {
    category: 'Tech',
    items: [
      { name: 'Lader (iPhone / Android)', checked: false },
      { name: 'Powerbank', checked: false },
      { name: 'Ekstra USB-kabel', checked: false },
      { name: 'Bluetooth speaker (backup)', checked: false },
      { name: 'Extension cord', checked: false },
    ],
  },
  {
    category: 'Mat & Drikke',
    items: [
      { name: 'Granola bars / snacks', checked: false },
      { name: 'Water bottles', checked: false },
      { name: 'Tyggemis / pastiller', checked: false },
      { name: 'Drink boxes with straws (for bridal makeup)', checked: false },
    ],
  },
  {
    category: 'Praktisk',
    items: [
      { name: 'Saks', checked: false },
      { name: 'Tape (vanlig + maskeringstape)', checked: false },
      { name: 'Superlim', checked: false },
      { name: 'Paraply', checked: false },
      { name: 'Stearinlys-tenner / lighter', checked: false },
      { name: 'Tusj / penn', checked: false },
      { name: 'Plastic bags (trash / wet clothes)', checked: false },
      { name: 'Lommelykt (kveldsfest)', checked: false },
      { name: 'Papirklips / binders', checked: false },
      { name: 'Ekstra tablekort (blanke)', checked: false },
    ],
  },
];

export const EmergencyKit = ({ kit, onChange }) => {
  const [addingTo, setAddingTo] = useState(null);
  const [newItem, setNewItem] = useState('');

  // Initialize with defaults if empty
  const categories = kit?.categories || [];
  const isInitialized = categories.length > 0;

  const initialize = () => {
    onChange({ ...kit, categories: DEFAULT_KIT.map(cat => ({ ...cat })), responsible: '', location: '' });
  };

  const toggleItem = (catIdx, itemIdx) => {
    const updated = [...categories];
    updated[catIdx] = {
      ...updated[catIdx],
      items: updated[catIdx].items.map((item, i) =>
        i === itemIdx ? { ...item, checked: !item.checked } : item
      ),
    };
    onChange({ ...kit, categories: updated });
  };

  const addItem = (catIdx) => {
    if (!newItem.trim()) return;
    const updated = [...categories];
    updated[catIdx] = {
      ...updated[catIdx],
      items: [...updated[catIdx].items, { name: newItem.trim(), checked: false }],
    };
    onChange({ ...kit, categories: updated });
    setNewItem('');
    setAddingTo(null);
  };

  const removeItem = (catIdx, itemIdx) => {
    const updated = [...categories];
    updated[catIdx] = {
      ...updated[catIdx],
      items: updated[catIdx].items.filter((_, i) => i !== itemIdx),
    };
    onChange({ ...kit, categories: updated });
  };

  const totalItems = categories.reduce((s, c) => s + c.items.length, 0);
  const checkedItems = categories.reduce((s, c) => s + c.items.filter(i => i.checked).length, 0);

  if (!isInitialized) {
    return (
      <div data-testid="emergency-kit" className="space-y-4">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Emergency Kit</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">Everything you need to handle the unexpected</p>
        </div>
        <div className="bg-white rounded-2xl border border-stone-100 p-8 text-center">
          <ShieldCheck className="w-10 h-10 text-primary mx-auto mb-4" />
          <h4 className="font-sans font-semibold text-stone-700 text-sm mb-2">Start med en komplett checkliste</h4>
          <p className="text-xs font-sans text-stone-400 mb-4 max-w-sm mx-auto">
            We have created a list of everything an emergency kit should contain. You can customize it afterwards.
          </p>
          <button type="button" onClick={initialize} data-testid="generate-emergency-kit-btn"
            className="bg-primary hover:bg-primary/90 text-white rounded-full px-6 py-2.5 text-xs font-sans font-semibold transition-colors">
            Generer Emergency Kit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="emergency-kit" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Emergency Kit</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">{checkedItems}/{totalItems} ting pakket</p>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-xl border border-stone-100 p-3">
        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
          <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${totalItems > 0 ? (checkedItems / totalItems) * 100 : 0}%` }} />
        </div>
        <p className="text-[10px] font-sans text-stone-400 mt-1.5">{Math.round(totalItems > 0 ? (checkedItems / totalItems) * 100 : 0)}% pakket</p>
      </div>

      {/* Responsible & Location */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase mb-0.5 block">Responsible</label>
            <input value={kit?.responsible || ''} onChange={e => onChange({ ...kit, responsible: e.target.value })}
              placeholder="Hvem have emergency kit?" className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
          </div>
          <div>
            <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase mb-0.5 block">Placement</label>
            <input value={kit?.location || ''} onChange={e => onChange({ ...kit, location: e.target.value })}
              placeholder="Hvor oppbevares den?" className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
          </div>
        </div>
      </div>

      {/* Categories */}
      {categories.map((cat, catIdx) => (
        <div key={cat.category} className="bg-white rounded-2xl border border-stone-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[10px] font-sans font-bold text-stone-600 uppercase tracking-wide">{cat.category}</h4>
            <span className="text-[9px] font-sans text-stone-400">{cat.items.filter(i => i.checked).length}/{cat.items.length}</span>
          </div>
          <div className="space-y-1">
            {cat.items.map((item, itemIdx) => (
              <div key={itemIdx} className="flex items-center gap-2 group py-0.5">
                <button type="button" onClick={() => toggleItem(catIdx, itemIdx)}
                  className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-all ${
                    item.checked ? 'bg-green-500 border-green-500' : 'border-stone-300 hover:border-primary'
                  }`}>
                  {item.checked && <Check className="w-3 h-3 text-white" />}
                </button>
                <span className={`text-xs font-sans flex-1 ${item.checked ? 'text-stone-400 line-through' : 'text-stone-700'}`}>{item.name}</span>
                <button type="button" onClick={() => removeItem(catIdx, itemIdx)}
                  className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-red-500 transition-all p-0.5">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          {addingTo === catIdx ? (
            <div className="flex items-center gap-2 mt-2">
              <input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="Ny ting..." autoFocus
                onKeyDown={e => e.key === 'Enter' && addItem(catIdx)}
                className="flex-1 text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
              <button type="button" onClick={() => addItem(catIdx)} className="text-primary text-xs font-sans font-semibold">Add</button>
              <button type="button" onClick={() => { setAddingTo(null); setNewItem(''); }} className="text-stone-400 text-xs">Cancel</button>
            </div>
          ) : (
            <button type="button" onClick={() => setAddingTo(catIdx)}
              className="flex items-center gap-1 text-[10px] font-sans text-stone-400 hover:text-primary mt-2 transition-colors">
              <Plus className="w-3 h-3" /> Add
            </button>
          )}
        </div>
      ))}
    </div>
  );
};
